import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import idx from 'idx';

import { ProductsContext } from '../../providers/ProductsProvider';
import { Button, CurrencyFormatter, LazyImage, Row, Text } from '../../../../common';
import { PageScrollContext } from '../../../../common/providers';
import { AppMenu, Filters, Img, Loader, useMenu } from '../../../common';
import Product from '../Product';
import useGTM, { Events } from '../../../../common/hooks/useGTM';
import { useCatalog } from '../../../common/sagas';

import saleIcon from '../../../../assets/icons/sale.svg';

import './styles.less';

const { REACT_APP_CLOUD_FRONT_URL } = process.env;

const ProductItems = () => {
  const { t } = useTranslation();
  const { loadProducts, productState } = useContext(ProductsContext);
  const { subscribeOnReachBottom, unsubscribeOnReachBottom } = useContext(PageScrollContext)
  const { products, isLoading } = productState;
  const [params, setParams] = useState({ offset: 0, limit: 18 });
  const { menuOpened, triggerMenu } = useMenu();
  const { pushEvent } = useGTM();
  const { gazePartition, worldPartition } = useCatalog();

  const gazeChildren = useMemo(() =>
    (gazePartition || []).map(({ parent, children }) => ({
      key: `section_${parent.id}`,
      label: parent.name,
      value: children.map(_ => _.id)
    })), [gazePartition]);

  const worldFilters = useMemo(() =>
    (worldPartition || []).map(({ parent, children }) => ({
      key: `section_${parent.id}`,
      label: parent.name,
      children: children.map(({ id, name }) => ({
        key: `section_${id}`,
        label: name,
        value: id
      }))
    })), [worldPartition]);

  const openedProduct = useMemo(
    () => {
      if (!products) return null;

      const productId = (/product-(\d+)/.exec(menuOpened) || [])[1];
      return products.edges.find(_ => `${_.id}` === `${productId}`);
    },
      [menuOpened, products]);

  const load = useCallback(
    () => {
      const isRefetch = params.offset === 0 &&
        idx(products, _ => !_.pageInfo.hasNextPage) &&
        !isLoading;

      const hasMoreData = (!products || products.pageInfo.hasNextPage) &&
        !isLoading;

      if (isRefetch || hasMoreData) {
        loadProducts(params, !params.offset);
        setParams({
          ...params,
          offset: params.offset + params.limit
        });
      }
    },
    [loadProducts, setParams, isLoading, params, products]
  );

  useEffect(() => {
    load();
  // eslint-disable-next-line
  }, [params]);

  useEffect(() => {
    subscribeOnReachBottom('products', load);
    return () => {
      unsubscribeOnReachBottom('products');
    };
  }, [load, subscribeOnReachBottom, unsubscribeOnReachBottom]);

  const handleOnProductClick = useCallback(
    productId => source => () => {
      triggerMenu(`product-${productId}`);
      pushEvent(
        Events.productsCategory.productDetailsClick({
          action: `${source}_click`,
          label: `product_${productId}`
        })
      );
    }, [pushEvent, triggerMenu]);

  const handleOnFilterChange = useCallback(filters => {
    setParams({
      offset: 0,
      limit: params.limit,
      ...filters.reduce((acc, { key, value, children }) => {
        if (children) {
          const sections = children
            .map(({ value }) => Array.isArray(value) ? value : [value])
            .reduce((acc, value) => acc.concat(value), []);

          return {
            ...acc,
            sections: `${acc.sections ? `${acc.sections},` : ''}${sections.join(',')}`
          }
        }

        return {
          ...acc,
          [key]: value
        };
      }, {})
    });
    window.scrollTo(0, 0);
  },
    [params]
  );

  return useMemo(() => {
    if (products) return (
      <>
        {openedProduct && (
          <AppMenu allowScroll>
            <div>
              <Product product={openedProduct} showLink showBackButton />
            </div>
          </AppMenu>
        )}
        <Row full direction="column" align="center" className="products-container">
          <Row className="products-filter-container">
            <Row full justify="center">
              <Filters
                items={[
                  { key: 'available', label: t`Available`, value: true },
                  { key: 'partitionId', label: 'GAZE LINE ⚡️', children: gazeChildren },
                  { key: 'sale', label: 'SALE 🤑', value: true },
                  ...worldFilters
                ]}
                onChange={handleOnFilterChange}
              />
            </Row>
          </Row>
          <Row className="product-items-container">
            {
              products && products.edges.map(product => {
                const { id, options, photos, price, info } = product;
                const isAvailable = !!options.length;

                return (
                  <div className="product-item" key={id}>
                    <div
                      className="product-item-photo"
                      onClick={handleOnProductClick(id)('image')}
                    >
                      {!!photos.length && (
                        <LazyImage
                          src={`${REACT_APP_CLOUD_FRONT_URL}/${photos[0].key}`}
                          className="product-photo"
                          effect="blur"
                          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        />
                      )}
                      {price.previousUah && (
                        <Img
                          src={saleIcon}
                          alt="sale"
                          style={{ position: 'absolute', top: 20, right: 20 }}
                          size={30}
                        />
                      )}
                    </div>
                    <div className="product-item-info">
                      <Row full direction="column">
                        <Row full mb={10}>
                          {
                            isAvailable
                              ? <Text p success bold capitalize>{t`Available`}</Text>
                              : <Text p danger bold capitalize>{t`Sold out`}</Text>
                          }
                        </Row>
                        <Row
                          full
                          mb={10}
                          align="start"
                          justify="space-between"
                          style={{ flexWrap: 'initial' }}
                        >
                          <Text h3 upper white newLine>
                            {info.title}
                          </Text>
                          {
                            isAvailable
                              ? <Button transparent onClick={handleOnProductClick(id)('buy')}>{t`Buy`}</Button>
                              : <Button transparent onClick={handleOnProductClick(id)('order')}>{t`On order`}</Button>
                          }
                        </Row>
                        <Row full align="center">
                          <Text h2 white bold newLine>
                            <CurrencyFormatter amount={price.currentUah} curr="UAH" />
                          </Text>
                        </Row>
                        {price.previousUah && (
                          <Row>
                            <Text h4 ghost italic newLine lineThrough>
                              <CurrencyFormatter amount={price.previousUah} curr="UAH" />
                            </Text>
                          </Row>
                        )}
                      </Row>
                    </div>
                  </div>
                );
              })
            }
          </Row>
          {products && (
            <Text h3 white center>
              {
                products.pageInfo.hasNextPage
                  ? `😻 ${t`Loading more products to you...`}`
                  : `😿 ${t`No more products found`}`
              }
            </Text>
          )}
          {isLoading && (
            <Row full direction="column" align="center" className="products-container">
              <Loader />
            </Row>
          )}
        </Row>
      </>
    );

    return null;
  }, [
    products,
    openedProduct,
    t,
    gazeChildren,
    worldFilters,
    handleOnFilterChange,
    isLoading,
    handleOnProductClick
  ]);
};

ProductItems.propTypes = {};

export default ProductItems;